import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  Card,
  Flex,
  Button,
  Badge,
  IconButton,
  Tooltip,
} from '@radix-ui/themes';
import {
  BellIcon,
  PlusIcon,
  Pencil1Icon,
  TrashIcon,
  PlayIcon,
  PauseIcon,
} from '@radix-ui/react-icons';
import { GetAlertsDocument } from '../../graphql/queries/GetAlerts.generated';
import { useUpdateAlertMutation } from '../../graphql/mutations/UpdateAlert.generated';
import { useDeleteAlertMutation } from '../../graphql/mutations/DeleteAlert.generated';
import { AlertStatus } from '../../types';
import TimeAgoTooltip from '../../components/TimeAgoTooltip';
import GraphQLTable, { Column } from '../../components/GraphQLTable/GraphQLTable';
import type { GetAlertsQuery } from '../../graphql/queries/GetAlerts.generated';

const StatusBadge = ({ status }: { status: AlertStatus }) => {
  const config = {
    [AlertStatus.Active]: { color: 'green' as const, label: 'Active' },
    [AlertStatus.Paused]: { color: 'amber' as const, label: 'Paused' },
    [AlertStatus.Draft]: { color: 'gray' as const, label: 'Draft' },
  };

  const { color, label } = config[status] || config[AlertStatus.Draft];

  return (
    <Badge color={color} variant="soft" size="1">
      {label}
    </Badge>
  );
};

type AlertNode = NonNullable<
  GetAlertsQuery['alerts']['edges'][number]['node']
>;

const AlertList: React.FC = () => {
  const navigate = useNavigate();
  const [updateAlert] = useUpdateAlertMutation();
  const [deleteAlert] = useDeleteAlertMutation();

  const handleToggleStatus = async (
    e: React.MouseEvent,
    alertId: string,
    currentStatus: AlertStatus,
  ) => {
    e.stopPropagation();
    const newStatus =
      currentStatus === AlertStatus.Active
        ? AlertStatus.Paused
        : AlertStatus.Active;
    await updateAlert({
      variables: { id: alertId, input: { status: newStatus } },
    });
  };

  const handleDelete = async (e: React.MouseEvent, alertId: string) => {
    e.stopPropagation();
    if (confirm('Are you sure you want to delete this alert?')) {
      await deleteAlert({ variables: { id: alertId } });
    }
  };

  const handleEditClick = (e: React.MouseEvent, alertId: string) => {
    e.stopPropagation();
    navigate(`/alerts/${alertId}`);
  };

  const columns: Column<AlertNode>[] = [
    {
      header: 'Name',
      accessor: 'name',
      render: ({ row }) => (
        <Flex direction="column">
          <Text size="2" weight="medium">
            {row.name}
          </Text>
          {row.description && (
            <Text size="1" color="gray">
              {row.description}
            </Text>
          )}
        </Flex>
      ),
    },
    {
      header: 'Status',
      accessor: 'status',
      render: ({ value }) => <StatusBadge status={value as AlertStatus} />,
    },
    {
      header: 'Triggers',
      accessor: 'triggerCount',
      render: ({ value }) => (
        <Badge color="amber" variant="soft">
          {value as number}
        </Badge>
      ),
    },
    {
      header: 'Last Triggered',
      accessor: 'lastTriggeredAt',
      render: ({ value }) =>
        value ? (
          <TimeAgoTooltip value={value as string} />
        ) : (
          <Text size="2" color="gray">
            —
          </Text>
        ),
    },
    {
      header: null,
      render: ({ row }) => (
        <Flex gap="1" justify="end" align="center" height="100%">
          {row.status !== AlertStatus.Draft && (
            <Tooltip
              content={row.status === AlertStatus.Active ? 'Pause' : 'Resume'}
            >
              <IconButton
                size="1"
                variant="ghost"
                color="gray"
                onClick={(e) => handleToggleStatus(e, row.id, row.status)}
              >
                {row.status === AlertStatus.Active ? (
                  <PauseIcon width={14} height={14} />
                ) : (
                  <PlayIcon width={14} height={14} />
                )}
              </IconButton>
            </Tooltip>
          )}
          <Tooltip content="Edit">
            <IconButton
              size="1"
              variant="ghost"
              color="gray"
              onClick={(e) => handleEditClick(e, row.id)}
            >
              <Pencil1Icon width={14} height={14} />
            </IconButton>
          </Tooltip>
          <Tooltip content="Delete">
            <IconButton
              size="1"
              variant="ghost"
              color="red"
              onClick={(e) => handleDelete(e, row.id)}
            >
              <TrashIcon width={14} height={14} />
            </IconButton>
          </Tooltip>
        </Flex>
      ),
      style: { width: '100px' },
    },
  ];

  return (
    <Box>
      <Box pb="4" px="1">
        <Flex align="center" justify="between">
          <Flex align="center" gap="3">
            <Box
              style={{
                padding: '8px',
                borderRadius: '8px',
                backgroundColor: 'var(--amber-3)',
                color: 'var(--amber-11)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <BellIcon width={24} height={24} />
            </Box>
            <Box>
              <Heading as="h1" size="5" weight="bold">
                Alerts
              </Heading>
              <Text size="2" color="gray">
                Get notified when specific conditions are met
              </Text>
            </Box>
          </Flex>
          <Button color="amber" onClick={() => navigate('/alerts/new')}>
            <PlusIcon />
            New Alert
          </Button>
        </Flex>
      </Box>

      <Card size="2">
        <Box
          style={{
            marginLeft: 'calc(-1 * var(--space-4))',
            marginRight: 'calc(-1 * var(--space-4))',
            marginBottom: 'calc(-1 * var(--space-4))',
          }}
        >
          <GraphQLTable
            query={GetAlertsDocument}
            columns={columns}
            connectionPath="alerts"
            size="2"
            removeBottomBorder
            noDataMessage="No alerts yet. Create your first alert to get started."
            onRowClick={(row) => navigate(`/alerts/${row.id}`)}
          />
        </Box>
      </Card>
    </Box>
  );
};

export default AlertList;
